import React, { useState, useEffect, Suspense } from "react";
// `lazy` → `lazyWithRetry` : retry automatique sur ChunkLoadError (chunk remplacé
// après un déploiement). Si l'échec persiste, l'erreur remonte à l'ErrorBoundary
// qui recharge la page (HTML + bundles cohérents). API identique à React.lazy.
import { lazyWithRetry as lazy } from "./utils/lazyWithRetry";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import RBACGate from "./components/RBACGate";
import HoraireAiguilleur from "./module-horaire-v2/HoraireAiguilleur";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Calendar } from "./components/ui/calendar";
import { useToast } from "./hooks/use-toast";
import { Toaster } from "./components/ui/toaster";
// Deux systemes de messages coexistent : Radix (useToast) et sonner (toast).
// Seul le Toaster Radix etait monte, si bien que les ~200 appels a `toast()` de
// sonner — paie, interventions, actifs, echanges de garde — ne s'affichaient
// JAMAIS : aucune erreur, aucune confirmation, aucun avertissement.
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { ConfirmDialogProvider } from "./components/ui/ConfirmDialog";
import VehiculeQRAction from './components/VehiculeQRAction';
import AutoDeclarationPublique from './components/AutoDeclarationPublique';
import RequetePublique from './components/requetes/RequetePublique';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import RemplacementResultat from './components/RemplacementResultat';
import RemplacementChoix from './components/RemplacementChoix';
import { useTenant } from "./contexts/TenantContext";
import { useAuth, AuthProvider } from "./contexts/AuthContext";
import { getTenantToken, buildApiUrl } from "./utils/api";
import { useInactivityLogout } from "./hooks/useInactivityLogout";
import { useGlobalModalScrollLock } from "./hooks/useModalScrollLock";
const SecteursMap = lazy(() => import("./components/SecteursMap"));
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiCall } from "./utils/api";
import PushNotificationService from "./services/pushNotifications";
import CacheManager from "./services/cacheManager";
import { initDeepLinkHandler } from "./services/deepLinking";
import { fr } from "date-fns/locale";
// ErrorBoundary global pour catcher les crashes silencieux dans les modules.
// Sans ce wrap, une exception non gérée fait apparaître un écran blanc sans
// message — diagnostic impossible. Cf. PR pages blanches Prévention.
import ErrorBoundary from "./components/ui/ErrorBoundary";

// Chart dynamique pour réduire bundle initial
const Chart = lazy(() => import("react-apexcharts"));
const RapportHeuresModal = lazy(() => import("./components/RapportHeuresModal"));
const AuditModal = lazy(() => import("./components/AuditModal"));
// Composants extraits pour réduire la taille de App.js
const Planning = lazy(() => import("./components/Planning"));
const Personnel = lazy(() => import("./components/Personnel"));
const ModuleEPI = lazy(() => import("./components/ModuleEPI"));
const Remplacements = lazy(() => import("./components/Remplacements"));
const Formations = lazy(() => import("./components/Formations"));
const MesDisponibilites = lazy(() => import("./components/MesDisponibilites"));
const Chat = lazy(() => import("./components/Chat"));
const MonProfil = lazy(() => import("./components/MonProfil"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const Sidebar = lazy(() => import("./components/Sidebar"));
const Rapports = lazy(() => import("./components/Rapports"));
const Prevention = lazy(() => import("./components/Prevention"));
const Batiments = lazy(() => import("./components/Batiments"));
const FormulairesProcedures = lazy(() => import('./components/FormulairesProcedures'));
const GestionPreventionnistes = lazy(() => import("./components/GestionPreventionnistes"));
const ImportBatiments = lazy(() => import("./components/ImportBatiments"));
const AuthComponentsModule = lazy(() => import("./components/AuthComponents"));
const GrillesInspectionModule = lazy(() => import("./components/GrillesInspectionComponents"));
const InspectionComponentsModule = lazy(() => import("./components/InspectionComponents"));
const PointEauModalsModule = lazy(() => import("./components/PointEauModals"));
// Import individual components from extracted modules
const GrillesInspection = lazy(() => import("./components/GrillesInspectionComponents").then(m => ({ default: m.GrillesInspection })));
const EditerGrille = lazy(() => import("./components/GrillesInspectionComponents").then(m => ({ default: m.EditerGrille })));
const CreateGrilleInspection = lazy(() => import("./components/GrillesInspectionComponents").then(m => ({ default: m.CreateGrilleInspection })));
const ListeInspections = lazy(() => import("./components/InspectionComponents").then(m => ({ default: m.ListeInspections })));
const NouvelleInspection = lazy(() => import("./components/InspectionComponents").then(m => ({ default: m.NouvelleInspection })));
const RealiserInspection = lazy(() => import("./components/InspectionComponents").then(m => ({ default: m.RealiserInspection })));
const DetailInspection = lazy(() => import("./components/InspectionComponents").then(m => ({ default: m.DetailInspection })));
const GestionNonConformites = lazy(() => import("./components/InspectionComponents").then(m => ({ default: m.GestionNonConformites })));
const CalendrierInspections = lazy(() => import("./components/InspectionComponents").then(m => ({ default: m.CalendrierInspections })));
const ModuleRapports = lazy(() => import("./components/ModuleRapports"));
const PointEauModal = lazy(() => import("./components/PointEauModals").then(m => ({ default: m.PointEauModal })));
const InspectionModal = lazy(() => import("./components/PointEauModals").then(m => ({ default: m.InspectionModal })));
// Import auth components
const AssignerPreventionniste = lazy(() => import("./components/MapComponents").then(m => ({ default: m.AssignerPreventionniste })));
const Login = lazy(() => import("./components/AuthComponents").then(module => ({ default: module.Login })));
const ForgotPassword = lazy(() => import("./components/AuthComponents").then(module => ({ default: module.ForgotPassword })));
const ResetPassword = lazy(() => import("./components/AuthComponents").then(module => ({ default: module.ResetPassword })));
import "./App.css";

// Composant d'installation PWA pour iOS
// Affiche une page d'installation SANS redirection automatique
// L'utilisateur doit d'abord ajouter le raccourci, puis cliquer pour continuer
const PWARedirect = () => {
  const { tenantSlug } = useParams();
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Vérifier si l'app est lancée depuis un raccourci (mode standalone)
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    if (isStandalone && tenantSlug) {
      // L'app est ouverte depuis un raccourci - rediriger vers le dashboard
      localStorage.setItem('profiremanager_last_tenant', tenantSlug);
      window.location.href = `/${tenantSlug}/dashboard`;
    }
  }, [tenantSlug]);
  
  const handleContinue = () => {
    if (tenantSlug) {
      localStorage.setItem('profiremanager_last_tenant', tenantSlug);
      window.location.href = `/${tenantSlug}/dashboard`;
    }
  };
  
  const tenantName = tenantSlug ? tenantSlug.charAt(0).toUpperCase() + tenantSlug.slice(1) : '';
  
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #DC2626 0%, #991b1b 100%)',
      color: 'white',
      textAlign: 'center',
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '1.5rem',
        maxWidth: '380px',
        width: '100%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🚒</div>
        <h1 style={{ color: '#DC2626', fontSize: '1.3rem', marginBottom: '0.25rem' }}>
          ProFireManager
        </h1>
        <h2 style={{ color: '#374151', fontSize: '1.1rem', marginBottom: '1rem', fontWeight: '600' }}>
          {tenantName}
        </h2>
        
        {/* Instructions d'installation */}
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '1rem',
          textAlign: 'left'
        }}>
          <p style={{ color: '#92400e', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            📱 Pour créer un raccourci :
          </p>
          <ol style={{ color: '#78350f', fontSize: '0.8rem', margin: 0, paddingLeft: '1.2rem', lineHeight: '1.6' }}>
            <li>Cliquez sur <strong>Partager</strong> (⬆️) en bas de Safari</li>
            <li>Sélectionnez <strong>"Sur l'écran d'accueil"</strong></li>
            <li>Nommez-le <strong>"{tenantName}"</strong></li>
            <li>Cliquez <strong>Ajouter</strong></li>
          </ol>
        </div>
        
        {/* Bouton pour continuer */}
        <button
          onClick={handleContinue}
          style={{
            width: '100%',
            padding: '0.9rem',
            background: '#DC2626',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '0.75rem'
          }}
        >
          Continuer vers {tenantName} →
        </button>
        
        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: 0 }}>
          Créez d'abord le raccourci, puis cliquez sur "Continuer"
        </p>
      </div>
      
      {/* Note pour plusieurs casernes */}
      <p style={{ 
        color: 'rgba(255,255,255,0.8)', 
        fontSize: '0.75rem', 
        marginTop: '1rem',
        maxWidth: '320px'
      }}>
        💡 Pour ajouter une autre caserne, utilisez l'URL :<br/>
        <code style={{ background: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: '4px' }}>
          /pwa/nom-caserne
        </code>
      </p>
    </div>
  );
};

// Lazy loading pour optimiser les performances
const Parametres = lazy(() => import("./components/Parametres"));
const SuperAdminDashboard = lazy(() => import("./components/SuperAdminDashboard"));
const MesEPI = lazy(() => import("./components/MesEPI"));
const PlansIntervention = lazy(() => import("./components/PlansIntervention"));
const BatimentDetailModal = lazy(() => import("./components/BatimentDetailModalNew"));
const ConflictResolutionModal = lazy(() => import("./components/ConflictResolutionModal"));
const GestionActifs = lazy(() => import("./components/GestionActifs"));
const GestionInterventions = lazy(() => import("./components/GestionInterventions"));
const PlanInterventionViewer = lazy(() => import("./components/PlanInterventionViewer"));
const ParametresPrevention = lazy(() => import("./components/ParametresPrevention"));
const PlanificationView = lazy(() => import("./components/PlanificationView"));
const CartePlanification = lazy(() => import("./components/CartePlanification"));
const NonConformites = lazy(() => import("./components/NonConformites"));
const InspectionTerrain = lazy(() => import("./components/InspectionTerrain"));
const ModulePaie = lazy(() => import("./components/ModulePaie"));
const EmailsHistory = lazy(() => import("./components/EmailsHistory"));
import OfflineManager from "./components/OfflineManager";
import BannerConsent from "./components/BannerConsent";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Composant de chargement
const LoadingComponent = () => (
  <div className="loading-component">
    <div className="loading-spinner"></div>
    <p>Chargement du module...</p>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Fonction utilitaire pour parser une date string "YYYY-MM-DD" en timezone local
const parseDateLocal = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Fonction utilitaire pour formater une date en string "YYYY-MM-DD" en timezone local
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Configure axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Fonctions utilitaires pour localStorage avec préfixe tenant (globales)
const getStorageKey = (key, tenantSlug) => {
  return tenantSlug ? `${tenantSlug}_${key}` : key;
};

window.getTenantItem = (key) => {
  const tenantSlug = window.location.pathname.split('/')[1];
  return localStorage.getItem(getStorageKey(key, tenantSlug));
};

window.setTenantItem = (key, value) => {
  const tenantSlug = window.location.pathname.split('/')[1];
  localStorage.setItem(getStorageKey(key, tenantSlug), value);
};


// Main Application Layout
const AppLayout = () => {
  // Hook pour déconnexion automatique après 10 min d'inactivité (web uniquement)
  useInactivityLogout();
  
  // Hook pour verrouiller le scroll automatiquement quand un modal est ouvert
  useGlobalModalScrollLock();
  
  // Persister la page active dans localStorage pour conserver après refresh
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('currentPage');
    return savedPage || 'dashboard';
  });
  // Flag (bug fix mai 2026) : true une fois que l'URL initiale a été lue
  // au démarrage par l'effet "déduire la page depuis l'URL". Avant ça, l'effet
  // qui synchronise l'URL ← currentPage ne doit PAS écraser l'URL, sinon il
  // efface les redirections cross-module (ex : Bâtiments → Prévention via
  // window.location.href = /tenant/prevention?action=inspecter&batiment=ID).
  // Symptôme : la page se réactualisait mais restait sur Bâtiments avec les
  // query params dans l'URL, et le modal de choix de grille ne s'ouvrait
  // qu'en cliquant manuellement sur le module Prévention.
  const [initialUrlSyncDone, setInitialUrlSyncDone] = useState(false);
  const [managingUserDisponibilites, setManagingUserDisponibilites] = useState(null);
  const [personnalisation, setPersonnalisation] = useState({
    logo_url: '',
    nom_service: '',
    afficher_profiremanager: true
  });
  const { user, tenant } = useAuth();
  const { tenantSlug } = useTenant();

  // Sauvegarder la page active dans localStorage à chaque changement.
  // ET synchroniser l'URL avec la page courante — sinon au refresh, le
  // useEffect ci-dessous (qui lit l'URL) écrase localStorage et renvoie
  // l'utilisateur sur le module figé dans l'URL initiale (souvent
  // 'prevention' ou 'dashboard').
  useEffect(() => {
    if (!currentPage) return;
    localStorage.setItem('currentPage', currentPage);

    // Bug fix mai 2026 : tant que la lecture initiale de l'URL n'a pas eu
    // lieu, on ne touche PAS à l'URL. Sinon on écrase une redirection
    // cross-module en cours (cf. flag initialUrlSyncDone ci-dessus).
    if (!initialUrlSyncDone) return;

    if (tenantSlug) {
      const targetPath = `/${tenantSlug}/${currentPage}`;
      // Préserver les query params (utilisés pour cross-module nav :
      // ?action=inspecter&batiment=ID, etc.)
      const currentSearch = window.location.search;
      const newUrl = `${targetPath}${currentSearch}`;
      if (window.location.pathname !== targetPath) {
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [currentPage, tenantSlug, initialUrlSyncDone]);

  // Charger la personnalisation
  useEffect(() => {
    const loadPersonnalisation = async () => {
      try {
        const response = await axios.get(`${API}/${tenantSlug}/personnalisation`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(getStorageKey('token', tenantSlug))}`
          }
        });
        setPersonnalisation(response.data);
      } catch (error) {
        console.error('Erreur chargement personnalisation:', error);
      }
    };

    if (tenantSlug && user) {
      loadPersonnalisation();
    }
  }, [tenantSlug, user]);

  // Détecter si l'utilisateur vient d'un QR code et rediriger vers le bon module
  useEffect(() => {
    const qrActionData = localStorage.getItem('qr_action');
    
    if (qrActionData) {
      try {
        const qrAction = JSON.parse(qrActionData);
        
        // Rediriger vers la page actifs pour ronde_securite OU inventaire
        if (qrAction.action === 'ronde_securite' || qrAction.action === 'inventaire' || qrAction.action === 'requetes') {
          setCurrentPage('actifs');
        }
        // Ne pas supprimer ici, laisser GestionActifs le faire après avoir ouvert le modal
      } catch (err) {
        console.error('❌ AppLayout - Erreur parsing qr_action:', err);
        localStorage.removeItem('qr_action');
      }
    }
  }, [user]);

  // Détecter si l'utilisateur vient d'un lien email et rediriger vers la bonne page
  useEffect(() => {
    if (!user) return;
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    const actionParam = urlParams.get('action');

    try {
      if (pageParam) {
        setCurrentPage(pageParam);
        // Nettoyer l'URL si pas d'action à conserver pour le sous-module
        if (!actionParam) {
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
        return;
      }

      // Fallback: déduire la page depuis le chemin URL (utile pour redirections cross-module avec query params)
      // Ex: /tenant/prevention?action=inspecter&batiment=ID
      const path = window.location.pathname;
      const pathParts = path.split('/').filter(Boolean);
      const moduleFromPath = pathParts[1]; // [0]=tenantSlug, [1]=module
      const knownModules = ['dashboard', 'personnel', 'actifs', 'interventions', 'paie',
        'planning', 'remplacements', 'disponibilites', 'formations',
        'batiments', 'prevention', 'rapports', 'parametres', 'profil', 'epi', 'chat'];
      if (moduleFromPath && knownModules.includes(moduleFromPath)) {
        const targetPage = moduleFromPath === 'epi' ? 'actifs' : moduleFromPath;
        setCurrentPage(targetPage);
        // Conserver les query params si présents (ils seront lus par le sous-module)
      }
    } finally {
      // Bug fix mai 2026 : signaler que la lecture initiale de l'URL est faite.
      // À partir de ce moment, l'effet qui synchronise URL ← currentPage peut
      // reprendre son rôle normal pour les navigations manuelles via sidebar.
      setInitialUrlSyncDone(true);
    }
  }, [user]);

  // Enregistrer le Service Worker PWA et le manifest dynamique
  useEffect(() => {
    if ('serviceWorker' in navigator && tenantSlug) {
      // Initialiser le CacheManager pour écouter les événements du SW
      CacheManager.init();
      
      // Écouter les mises à jour du Service Worker
      const unsubscribe = CacheManager.addListener((eventType, data) => {
        if (eventType === 'newVersion') {
          console.log('[App] Nouvelle version disponible - rechargement automatique');
          // Forcer la mise à jour et recharger
          CacheManager.forceUpdate().then(() => {
            window.location.reload();
          });
        } else if (eventType === 'update') {
          console.log('[App] Service Worker mis à jour vers:', data.version);
        }
      });
      
      // Enregistrer le Service Worker
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('✅ Service Worker enregistré:', registration.scope);
          
          // Vérifier les mises à jour toutes les heures
          setInterval(() => {
            registration.update();
          }, 60 * 60 * 1000);
        })
        .catch((error) => {
          console.log('❌ Erreur Service Worker:', error);
        });

      // Écouter les messages du Service Worker (pour la navigation depuis notifications)
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('[App] Message du Service Worker:', event.data);
        if (event.data && event.data.type === 'NAVIGATE') {
          const url = event.data.url || '';
          const data = event.data.data || {};
          
          // Parser l'URL pour déterminer la page
          const urlParts = url.split('/').filter(Boolean);
          const pageName = urlParts[1] || urlParts[0] || '';
          
          // Navigation vers la bonne page
          if (pageName === 'remplacements' || url.includes('/remplacements')) {
            setCurrentPage('remplacements');
          } else if (pageName === 'actifs' || url.includes('/actifs')) {
            setCurrentPage('actifs');
          } else if (pageName === 'epi' || url.includes('/epi')) {
            setCurrentPage('actifs'); // EPI est dans gestion des actifs
          } else if (pageName === 'dashboard' || url.includes('/dashboard')) {
            setCurrentPage('dashboard');
          } else if (pageName === 'planning' || url.includes('/planning')) {
            setCurrentPage('planning');
          } else if (pageName === 'batiments' || url.includes('/batiments')) {
            setCurrentPage('batiments');
          } else if (pageName === 'prevention' || url.includes('/prevention')) {
            setCurrentPage('prevention');
          }
          
          console.log('[App] Navigation vers:', pageName);
        }
      });

      // Mettre à jour le manifest dynamique
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        manifestLink.href = `${process.env.REACT_APP_BACKEND_URL}/api/${tenantSlug}/manifest.json`;
      } else {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = `${process.env.REACT_APP_BACKEND_URL}/api/${tenantSlug}/manifest.json`;
        document.head.appendChild(link);
      }
      
      // Cleanup
      return () => {
        unsubscribe();
      };
      
      // Initialiser le gestionnaire de deep links pour les apps mobiles
      initDeepLinkHandler((path) => {
        // Navigation basée sur le path du deep link
        console.log('[DeepLink] Navigating to:', path);
        const pathParts = path.split('/').filter(Boolean);
        const module = pathParts[0] || '';
        
        // Mapper le module vers la page
        const moduleToPage = {
          'remplacements': 'remplacements',
          'planning': 'planning',
          'personnel': 'personnel',
          'disponibilites': 'disponibilites',
          'actifs': 'actifs',
          'epi': 'actifs',
          'interventions': 'interventions',
          'formations': 'formations',
          'dashboard': 'dashboard',
          'batiments': 'batiments',
          'prevention': 'prevention',
          'rapports': 'rapports'
        };
        
        const targetPage = moduleToPage[module] || 'dashboard';
        setCurrentPage(targetPage);
      }, tenantSlug);
    }
  }, [tenantSlug]);

  // RBAC : helper qui wrappe le rendu d'un module avec RBACGate.
  // Garantit qu'un composant ne se rend JAMAIS si l'utilisateur n'a pas la permission,
  // même si l'URL ou le state est forcé. Admin = bypass automatique côté hook.
  const gated = (moduleId, element) => (
    <RBACGate moduleId={moduleId} tenantSlug={tenantSlug} user={user}>
      {element}
    </RBACGate>
  );

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return gated('dashboard', <Dashboard setCurrentPage={setCurrentPage} />);
      case 'personnel':
        return gated('personnel',
          <Personnel
            setCurrentPage={setCurrentPage}
            setManagingUserDisponibilites={setManagingUserDisponibilites}
          />
        );
      case 'actifs':
        return gated('actifs',
          <Suspense fallback={<LoadingComponent />}>
            <GestionActifs user={user} ModuleEPI={ModuleEPI} />
          </Suspense>
        );
      case 'interventions':
        return gated('interventions',
          <Suspense fallback={<LoadingComponent />}>
            <GestionInterventions user={user} tenantSlug={tenantSlug} />
          </Suspense>
        );
      case 'paie':
        return gated('paie',
          <Suspense fallback={<LoadingComponent />}>
            <ModulePaie tenant={tenantSlug} user={user} />
          </Suspense>
        );
      case 'planning':
        return gated('planning', <HoraireAiguilleur classique={<Planning />} />);
      case 'remplacements':
        return gated('remplacements', <Remplacements />);
      case 'disponibilites':
        return gated('disponibilites',
          <MesDisponibilites
            user={user}
            managingUser={managingUserDisponibilites}
            setCurrentPage={setCurrentPage}
            setManagingUserDisponibilites={setManagingUserDisponibilites}
          />
        );
      case 'chat':
        // Module accessible à tous les utilisateurs authentifiés du tenant
        return (
          <Suspense fallback={<LoadingComponent />}>
            <Chat />
          </Suspense>
        );
      case 'formations':
        return gated('formations', <Formations />);
      case 'documents':
        // PAS de `gated('documents', …)` : l'onglet « Mes lectures » doit
        // rester atteignable même quand le module est fermé pour le rôle.
        // C'est le miroir d'une décision serveur — `/documents/mes-lectures` et
        // `.../accuser` n'ont volontairement aucun garde de permission, pour
        // qu'un pompier ne puisse jamais être empêché de signer sa propre
        // obligation. Le gate annulerait cette décision. Les autres onglets de
        // la page sont gardés un par un, à l'intérieur du composant.
        return (
          <Suspense fallback={<LoadingComponent />}>
            <FormulairesProcedures tenantSlug={tenantSlug} user={user} />
          </Suspense>
        );
      case 'batiments':
        return gated('batiments',
          <Suspense fallback={<LoadingComponent />}>
            <Batiments setCurrentPage={setCurrentPage} />
          </Suspense>
        );
      case 'prevention':
        return gated('prevention',
          <ErrorBoundary label="Erreur dans le module Prévention">
            <Suspense fallback={<LoadingComponent />}>
              <Prevention />
            </Suspense>
          </ErrorBoundary>
        );
      case 'rapports':
        return gated('rapports',
          <Suspense fallback={<LoadingComponent />}>
            <Rapports />
          </Suspense>
        );
      case 'parametres':
        return gated('parametres',
          <Suspense fallback={<LoadingComponent />}>
            <Parametres user={user} tenantSlug={tenantSlug} />
          </Suspense>
        );
      case 'mesepi':
        // mesepi est accessible à tous les utilisateurs authentifiés
        return (
          <Suspense fallback={<LoadingComponent />}>
            <MesEPI user={user} />
          </Suspense>
        );
      case 'monprofil':
        // monprofil est accessible à tous les utilisateurs authentifiés
        return <MonProfil />;
      case 'emails-history':
        return gated('parametres',
          <Suspense fallback={<LoadingComponent />}>
            <EmailsHistory />
          </Suspense>
        );
      default:
        return <Dashboard setCurrentPage={setCurrentPage} />;
    }
  };

  return (
    <div className="app-layout">
      <Sidebar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        tenant={tenant}
        personnalisation={personnalisation}
      />
      <main className="main-content">
        <div className="main-header" style={{
          padding: '1rem 2rem',
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {personnalisation.logo_url && (
              <img 
                src={personnalisation.logo_url} 
                alt="Logo du service" 
                style={{ height: '50px', maxWidth: '200px', objectFit: 'contain' }}
              />
            )}
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                {personnalisation.nom_service || tenant?.nom || 'ProFireManager'}
              </h1>
            </div>
          </div>
        </div>
        {/* ErrorBoundary GLOBAL : capte les ChunkLoadError de n'importe quel module
            lazy (pas seulement Prévention) → écran « Mise à jour… » + rechargement
            automatique au lieu d'une page blanche. */}
        <ErrorBoundary label="Erreur de chargement">
          {renderCurrentPage()}
        </ErrorBoundary>
      </main>
    </div>
  );
};

// Main App Component
const App = () => {
  const { user, tenant, loading, logout } = useAuth();
  const { isSuperAdmin, tenantSlug } = useTenant();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  // Si l'utilisateur est un Super-Admin, afficher le dashboard super-admin
  if (user && isSuperAdmin) {
    return (
      <div className="App">
        <Suspense fallback={<LoadingComponent />}>
          <SuperAdminDashboard onLogout={logout} />
        </Suspense>
        <Toaster />
        <SonnerToaster />
      </div>
    );
  }

  // Sinon, afficher l'interface normale
  return (
    <div className="App">
      {user && <OfflineManager tenant={tenant} />}
      {/* Bandeau Loi 25 : affiché tant que la version courante de la politique n'est pas acceptée */}
      {user && <BannerConsent tenantSlug={tenantSlug} currentUser={user} />}
      {/* PWAInstallPrompt désactivé - app native disponible */}
      {user ? <AppLayout /> : <Login />}
      <Toaster />
      <SonnerToaster />
    </div>
  );
};

// Root App with Providers
const AppWithProviders = () => {
  return (
    <BrowserRouter>
      <ConfirmDialogProvider>
        <Routes>
          {/* Routes spécifiques QR - DOIT être avant les routes avec paramètres */}
          <Route path="/qr/:tenantSlug/vehicule/:vehiculeId" element={
            <VehiculeQRAction />
          } />
          {/* Formulaire PUBLIC d'auto-déclaration citoyenne (hors AuthProvider) */}
          <Route path="/declaration/:tenantSlug" element={<AutoDeclarationPublique />} />
          {/* Requête de véhicule : lien personnel reçu par courriel (travaux publics sans compte) */}
          <Route path="/requete/:tenantSlug/:jeton" element={<RequetePublique />} />
          {/* Route spéciale pour installation PWA sur iOS */}
          <Route path="/pwa/:tenantSlug" element={<PWARedirect />} />
          {/* Route pour résultat d'action remplacement via email */}
          <Route path="/remplacement-resultat" element={<RemplacementResultat />} />
          {/* Route pour choix accepter/refuser via SMS */}
          <Route path="/remplacement-choix" element={<RemplacementChoix />} />
          <Route path="/:tenant/reset-password" element={
            <AuthProvider>
              <ResetPassword />
              <Toaster />
              <SonnerToaster />
            </AuthProvider>
          } />
          <Route path="*" element={
            <AuthProvider>
              <App />
            </AuthProvider>
          } />
        </Routes>
      </ConfirmDialogProvider>
    </BrowserRouter>
  );
};

export default AppWithProviders;